import { ICellRendererParams } from '@ag-grid-community/core'

interface MultiLogFileCellRendererProps {
  params: ICellRendererParams
  // When true, each line is a downloadable link (S3 URLs). When false, each
  // line is shown as plain text (FTP remote paths - not fetchable from the
  // browser). Defaults to true.
  downloadable?: boolean
}

/**
 * Renders a comma-separated list of values (e.g. Talabat's multiple chunked
 * files) one per line, instead of showing/linking only the last segment when
 * given the whole joined string. Paired with `autoHeight: true` on the
 * column so the row grows to fit every line - lines are packed tightly
 * (no gap, no extra vertical padding) so a multi-line cell doesn't grow
 * taller than it needs to.
 */
const MultiLogFileCellRenderer = ({
  params,
  downloadable = true,
}: MultiLogFileCellRendererProps) => {
  const values: string[] = (params.value || '')
    .split(',')
    .map((value: string) => value.trim())
    .filter(Boolean)

  if (values.length === 0) {
    return <>-</>
  }

  return (
    <div className="flex flex-col justify-center py-1 leading-tight">
      {values.map((value) => {
        if (!downloadable) {
          return (
            <span key={value} className="truncate" title={value}>
              {value}
            </span>
          )
        }

        // Show just the file name so a long S3 URL doesn't blow out the
        // column width - the full URL is still available on hover via the
        // title tooltip.
        const fileName = value.split('/').pop() || value

        return (
          <a
            key={value}
            download
            href={value}
            className="truncate hover:underline"
            title={value}
          >
            {fileName}
          </a>
        )
      })}
    </div>
  )
}

export default MultiLogFileCellRenderer
